import { useState, useEffect } from 'react';

// Cache key for localStorage
const CACHE_KEY = 'moonui_license_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const OFFLINE_GRACE_PERIOD = 7 * 24 * 60 * 60 * 1000; // 7 days

interface LicenseCache {
  valid: boolean;
  hasLifetimeAccess: boolean;
  timestamp: number;
  cacheUntil?: number;
}

export function useSubscription() {
  const [isLoading, setIsLoading] = useState(true);
  const [hasProAccess, setHasProAccess] = useState(true); // Default to true for better UX
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  
  useEffect(() => {
    // Check cache first
    const checkLicense = async () => {
      try {
        // Get cached license
        const cached = localStorage.getItem(CACHE_KEY);
        
        if (cached) {
          const cacheData: LicenseCache = JSON.parse(cached);
          const now = Date.now();
          
          // Check if cache is still valid
          if (now - cacheData.timestamp < CACHE_DURATION) {
            setHasProAccess(cacheData.valid && cacheData.hasLifetimeAccess);
            setIsAuthenticated(cacheData.valid);
            setIsLoading(false);
            return;
          }
          
          // Check if within offline grace period
          if (now - cacheData.timestamp < OFFLINE_GRACE_PERIOD) {
            // Try to validate but don't block if offline
            validateLicense().catch(() => {
              // If validation fails, use cached data during grace period
              setHasProAccess(cacheData.valid && cacheData.hasLifetimeAccess);
              setIsAuthenticated(cacheData.valid);
            });
            setIsLoading(false);
            return;
          }
        }
        
        // No valid cache, validate license
        await validateLicense();
      } catch (error) {
        console.error('License check error:', error);
        // In case of error, default to allowing access
        setHasProAccess(true);
        setIsAuthenticated(true);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkLicense();
  }, []);
  
  const validateLicense = async () => {
    // Get API token from environment or localStorage
    const token = process.env.NEXT_PUBLIC_MOONUI_AUTH_TOKEN || 
                  localStorage.getItem('moonui_auth_token');
    
    if (!token) {
      // No token, assume development environment
      setHasProAccess(true);
      setIsAuthenticated(true);
      return;
    }
    
    try {
      const response = await fetch('https://moonui.dev/api/auth/validate', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Cache the response
        const cacheData: LicenseCache = {
          valid: data.valid,
          hasLifetimeAccess: data.user?.hasLifetimeAccess || false,
          timestamp: Date.now(),
          cacheUntil: data.cacheUntil ? new Date(data.cacheUntil).getTime() : undefined
        };
        
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
        
        setHasProAccess(data.valid && (data.user?.hasLifetimeAccess || data.user?.features?.includes('pro_components')));
        setIsAuthenticated(data.valid);
      } else {
        // Invalid response, clear cache
        localStorage.removeItem(CACHE_KEY);
        setHasProAccess(false);
        setIsAuthenticated(false);
      }
    } catch (error) {
      // Network error, use cached data if available
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const cacheData: LicenseCache = JSON.parse(cached);
        const now = Date.now();
        
        // Allow access during grace period even if network is down
        if (now - cacheData.timestamp < OFFLINE_GRACE_PERIOD) {
          setHasProAccess(cacheData.valid && cacheData.hasLifetimeAccess);
          setIsAuthenticated(cacheData.valid);
          return;
        }
      }
      
      // No cache or expired, deny access
      setHasProAccess(false);
      setIsAuthenticated(false);
    }
  };
  
  return {
    isLoading,
    isAuthenticated,
    isAdmin: false,
    hasProAccess,
    subscriptionPlan: hasProAccess ? "lifetime" as const : "free" as const,
    subscription: {
      status: hasProAccess ? "active" as const : "inactive" as const,
      plan: hasProAccess ? "lifetime" as const : "free" as const,
    },
  };
}